<template>
    <div>
        <b-modal id="driver-qr-link-modal" centered no-close-on-backdrop :footer-class="'d-block p-3 m-0'"
            :body-class="'equipment-edit-modal'" no-close-on-esc>
            <template #modal-header="{ close }">
                <h5 class="modal-title">
                    {{ $t('driverQRLink.title') }}
                </h5>
                <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
                    Close Modal
                </b-button>
            </template>
            <div class="body">
                <b-form-group :label="$t('driverQRLink.linkLabel')" class="text-left mt-3 mb-0 position-relative">
                    <b-skeleton v-if="loading" height="86px"></b-skeleton>
                    <template v-else>
                        <b-form-textarea :value="url" rows="2" readonly class="resize-none f-12 pr-2"
                            :placeholder="$t('driverQRLink.noLink')" style="padding-right: 10% !important"></b-form-textarea>
                        <!-- Same inline copy icon as the Send App link. -->
                        <svg v-if="url" @click="copyLink()" class="copy-link-icon cursor-pointer" width="15" height="15"
                            viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <title>{{ $t('driverQRLink.copy') }}</title>
                            <path
                                d="M9.84375 10.7813V12.8906C9.84375 13.2788 9.52875 13.5938 9.14062 13.5938H3.04688C2.86039 13.5938 2.68155 13.5197 2.54969 13.3878C2.41783 13.2559 2.34375 13.0771 2.34375 12.8906V4.92188C2.34375 4.53375 2.65875 4.21875 3.04688 4.21875H4.21875C4.53286 4.21854 4.84644 4.24446 5.15625 4.29625M9.84375 10.7813H11.9531C12.3412 10.7813 12.6562 10.4663 12.6562 10.0781V7.03125C12.6562 4.24375 10.6294 1.93063 7.96875 1.48375C7.65894 1.43196 7.34536 1.40604 7.03125 1.40625H5.85938C5.47125 1.40625 5.15625 1.72125 5.15625 2.10938V4.29625M9.84375 10.7813H5.85938C5.67289 10.7813 5.49405 10.7072 5.36219 10.5753C5.23033 10.4434 5.15625 10.2646 5.15625 10.0781V4.29625M12.6562 8.4375V7.26563C12.6562 6.70619 12.434 6.16966 12.0384 5.77407C11.6428 5.37849 11.1063 5.15625 10.5469 5.15625H9.60938C9.42289 5.15625 9.24405 5.08217 9.11219 4.95031C8.98033 4.81845 8.90625 4.63961 8.90625 4.45313V3.51563C8.90625 3.23862 8.85169 2.96432 8.74568 2.7084C8.63968 2.45248 8.4843 2.21995 8.28843 2.02407C8.09255 1.8282 7.86002 1.67282 7.6041 1.56682C7.34818 1.46081 7.07388 1.40625 6.79688 1.40625H6.09375"
                                stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </template>
                </b-form-group>
                <!-- Rendered off-screen only to turn the link into the QR image for the PDF. -->
                <qriously v-if="url" class="d-none" :value="url" :size="600" ref="qrcode" />
            </div>
            <template #modal-footer>
                <div class="modal-footer border-0 p-0 mb-0 mt-0 d-flex align-items-center justify-content-end">
                    <!-- Only once a link exists; on the left, away from the other actions. -->
                    <b-button v-if="url && !loading" class="mr-auto h-40px w-100px" variant="danger" :disabled="busy"
                        @click="showDeleteModal = true">{{ $t('driverQRLink.delete') }}</b-button>
                    <b-button class="mx-0 h-40px w-100px m-0" variant="secondary"
                        @click="$bvModal.hide('driver-qr-link-modal')">{{ $t('driverQRLink.close') }}</b-button>
                    <!-- One main action: Download QR once a link exists, Generate before that. -->
                    <b-button v-if="url" class="mx-0 ml-3 mb-0 h-40px px-3 m-0" variant="primary"
                        @click="downloadQR()">{{ $t('driverQRLink.download') }}</b-button>
                    <b-button v-else class="mx-0 ml-3 mb-0 h-40px px-3 m-0" variant="primary"
                        :disabled="loading || busy" @click="generateLink()">
                        {{ $t('driverQRLink.generate') }}
                    </b-button>
                </div>
            </template>
        </b-modal>
        <DeleteModal v-if="showDeleteModal" :message="$t('driverQRLink.confirmDelete')"
            @confirm="deleteLink($event)" />
    </div>
</template>
<script>
import { mapActions } from "vuex";
import DeleteModal from "@/components/DeleteModal.vue";
import generateQRCode from "../../services/generateDriverQRCode";

export default {
    name: "DriverQRLink",
    components: { DeleteModal },
    props: {
        driverId: { type: [Number, String], required: true },
        driverName: { type: String, default: "" },
    },
    data() {
        return {
            loading: false,
            busy: false,
            url: "",
            showDeleteModal: false,
        };
    },
    watch: {
        // The drawer can switch straight to another driver without closing.
        driverId: {
            handler() {
                this.loadLink();
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions(["getDriverQrLogin", "createDriverQrLogin", "deleteDriverQrLogin", "setNotification", "setLoader"]),
        notifyError(error, fallbackKey) {
            return this.setNotification({
                msg: typeof error === "string" ? error : (error && error.detail) || this.$t(fallbackKey),
                type: "error",
                color: "red",
            });
        },
        // Checked when the driver's edit drawer opens (this component mounts with
        // it), so the popup already has the link by the time it is opened.
        async loadLink() {
            this.loading = true;
            this.url = "";
            try {
                const data = await this.getDriverQrLogin(this.driverId);
                this.url = (data && data.exists && data.url) || "";
            } catch (error) {
                // Quiet: this runs whenever a driver is opened, not on request. The
                // popup just offers Generate, which reports its own errors.
                console.log(error);
            } finally {
                this.loading = false;
            }
        },
        async generateLink() {
            this.busy = true;
            try {
                const data = await this.createDriverQrLogin(this.driverId);
                this.url = (data && data.url) || "";
            } catch (error) {
                await this.notifyError(error, 'driverQRLink.generateFailed');
            } finally {
                this.busy = false;
            }
        },
        async deleteLink(confirmed) {
            this.showDeleteModal = false;
            if (!confirmed) return;
            this.busy = true;
            try {
                this.setLoader(true);
                await this.deleteDriverQrLogin(this.driverId);
                this.url = "";
                await this.setNotification({
                    msg: this.$t('driverQRLink.deleted'),
                    type: "success",
                    color: "green",
                });
            } catch (error) {
                await this.notifyError(error, 'driverQRLink.deleteFailed');
            } finally {
                this.setLoader(false);
                this.busy = false;
            }
        },
        async copyLink() {
            try {
                await navigator.clipboard.writeText(this.url);
                await this.setNotification({
                    msg: this.$t('driverQRLink.copied'),
                    type: "success",
                    color: "green",
                });
            } catch (e) {
                console.log(e);
            }
        },
        // The same printable QR PDF as before, now encoding the login link.
        async downloadQR() {
            await this.$nextTick();
            const qrImage = this.$refs.qrcode.qrious._canvasRenderer.element.toDataURL();
            generateQRCode(this.driverName, this.driverId, `${this.driverName}-qrcode`, qrImage);
        },
    },
};
</script>
<style>
/* Not scoped: b-modal renders outside this component, so scoped rules don't
   reach it. Prefixed with the modal id so it can't leak elsewhere. */
#driver-qr-link-modal .copy-link-icon {
    position: absolute;
    right: 10px;
    top: 20px;
    z-index: 10;
}
</style>
